import { useT, type MessageKey } from '../../i18n/index.js';

/**
 * Les portes d'entrée de la page Model (ADR-0017, + Objectifs ADR-0028).
 *
 * « Model Zoo » (terme standard du domaine, edge compris) regroupe **vos modèles** et le
 * **catalogue pré-entraîné** ; « Construire un modèle » porte l'import.
 *
 * « Objectifs » et « Cas d'usages » ne sont plus des onglets : l'objectif est devenu la **première
 * étape** du bloc processus « Créer un modèle » en tête de page (`ModelProcessBlock`), et le
 * catalogue de cas d'usages y est présenté comme une **sous-catégorie** de l'objectif (au moment
 * de le démarrer). Voir ADR-0028.
 */
export type ModelSection = 'modelZoo' | 'build';

interface SectionDef {
  id: ModelSection;
  /** Fragment d'URL (#…) qui rend la section partageable et rechargeable. */
  hash: string;
  labelKey: MessageKey;
}

export const MODEL_SECTIONS: readonly SectionDef[] = [
  { id: 'modelZoo', hash: 'model-zoo', labelKey: 'model.section.modelZoo' },
  { id: 'build', hash: 'construire', labelKey: 'model.section.build' },
];

/** Section active déduite du fragment d'URL ; « Model Zoo » par défaut. */
export function sectionFromHash(hash: string): ModelSection {
  const target = hash.replace(/^#/, '');
  return MODEL_SECTIONS.find((s) => s.hash === target)?.id ?? 'modelZoo';
}

/**
 * Barre de liens juxtaposés sous l'en-tête. Ce sont de vrais hyperliens (`href="#…"`) : le
 * changement de fragment est capté par la page (voir `ModelsPage`), ce qui rend chaque section
 * atteignable au clic, au rechargement et par lien partagé, sans routeur ni onglet Carbon.
 */
export function ModelSectionNav({ active }: { active: ModelSection }) {
  const t = useT();
  return (
    <nav className="ddp-modelnav" aria-label="Sections des modèles">
      {MODEL_SECTIONS.map((section) => (
        <a
          key={section.id}
          href={`#${section.hash}`}
          className={`ddp-modelnav__link${section.id === active ? ' is-active' : ''}`}
          aria-current={section.id === active ? 'page' : undefined}
        >
          {t(section.labelKey)}
        </a>
      ))}
    </nav>
  );
}
